import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  alpha,
  InputAdornment,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { ErpContext } from '../../context/ErpContext.js';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LockIcon from '@mui/icons-material/Lock';

const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

export default function TablaTipoTransaccionComponent() {
  const {
    getTipoTransaccion,
    tipoTransaccion,
    gestionTipoTransaccionTabla,
    gestionTipoTransaccionTablaData,
    updateTipoTransaccion,
    deleteTipoTransaccion,
    isNavbarCollapsed,
  } = useContext(ErpContext);

  // Estados para edición
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [editDescripcion, setEditDescripcion] = useState("");
  const [editVerEnTransaccion, setEditVerEnTransaccion] = useState(false);
  const [editVerEnCaja, setEditVerEnCaja] = useState(false);
  const [editVerEnColumna, setEditVerEnColumna] = useState("");

  // Estados para eliminación
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingTipo, setDeletingTipo] = useState(null);
  
  const [loading, setLoading] = useState(false);

  // Usar gestionTipoTransaccionTablaData si está disponible, sino tipoTransaccion
  const dataToDisplay = gestionTipoTransaccionTablaData && gestionTipoTransaccionTablaData.length > 0 
    ? gestionTipoTransaccionTablaData 
    : tipoTransaccion;

  useEffect(() => {
    // Solo cargar getTipoTransaccion si no hay datos de gestion
    if (!gestionTipoTransaccionTablaData || gestionTipoTransaccionTablaData.length === 0) {
      getTipoTransaccion();
    }
  }, []);

  const handleOpenEditDialog = (tipo) => {
    setEditingTipo(tipo);
    setEditDescripcion(tipo.descripcion);
    setEditVerEnTransaccion(tipo.verEnTransaccion || false);
    setEditVerEnCaja(tipo.verEnCaja || false);
    setEditVerEnColumna(tipo.verEnColumna || "");
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingTipo(null);
    setEditDescripcion("");
    setEditVerEnTransaccion(false);
    setEditVerEnCaja(false);
    setEditVerEnColumna("");
  };

  const handleUpdateTipo = async () => {
    if (!editDescripcion.trim()) {
      alert("Por favor ingrese una descripción");
      return;
    }

    setLoading(true);
    try {
      await updateTipoTransaccion(editingTipo.id, {
        descripcion: editDescripcion,
        verEnTransaccion: editVerEnTransaccion,
        verEnCaja: editVerEnCaja,
        verEnColumna: editVerEnCaja ? editVerEnColumna : null,
      });
      // Refrescar datos de gestión y tipo transacción
      await Promise.all([
        gestionTipoTransaccionTabla(),
        getTipoTransaccion()
      ]);
      handleCloseEditDialog();
    } catch (err) {
      console.error("Error al actualizar:", err);
      if (err.response && err.response.status === 403) {
        alert("No se pueden editar los tipos de transacción del sistema (ID 1-6)");
      } else {
        alert("Error al actualizar el tipo de transacción");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = (tipo) => {
    setDeletingTipo(tipo);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeletingTipo(null);
  };

  const handleDeleteTipo = async () => {
    setLoading(true);
    try {
      await deleteTipoTransaccion(deletingTipo.id);
      // Refrescar datos de gestión y tipo transacción
      await Promise.all([
        gestionTipoTransaccionTabla(),
        getTipoTransaccion()
      ]);
      handleCloseDeleteDialog();
    } catch (err) {
      console.error("Error al eliminar:", err);
      if (err.response && err.response.status === 403) {
        alert("No se pueden eliminar los tipos de transacción del sistema (ID 1-6)");
      } else {
        alert("Error al eliminar el tipo de transacción");
      }
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'descripcion', headerName: 'Descripción', width: 200, flex: 1 },
    {
      field: 'verEnTransaccion',
      headerName: 'Ver en Transacción',
      width: 140,
      renderCell: (params) => (params.value ? '✓' : ''),
    },
    {
      field: 'verEnCaja',
      headerName: 'Ver en Caja',
      width: 110,
      renderCell: (params) => (params.value ? '✓' : ''),
    },
    {
      field: 'verEnColumna',
      headerName: 'Columna',
      width: 120,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      renderCell: (params) => {
        const isSystemType = params.row.id <= 20; // bloqueo de edicion ****************** #######################################################################################
        
        return (
          <Box>
            {isSystemType ? (
              <Tooltip title="Solo lectura (tipo del sistema)">
                <IconButton
                  onClick={() => handleOpenEditDialog(params.row)}
                  color="primary"
                  size="small"
                >
                  <LockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Editar">
                  <IconButton
                    onClick={() => handleOpenEditDialog(params.row)}
                    color="primary"
                    size="small"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar">
                  <IconButton
                    onClick={() => handleOpenDeleteDialog(params.row)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <Paper sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <DataGrid
          key={isNavbarCollapsed ? "collapsed" : "expanded"}
          rows={dataToDisplay}
          columns={columns}
          getRowId={(row) => row.id}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50]}
          sx={{ 
            border: 0,
            flexGrow: 1,
            '& .MuiDataGrid-virtualScroller': {
              overflowY: 'auto',
            },
          }}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0
              ? "row-color-alt"
              : "row-color-default"
          }
        />
      </Paper>

      {/* Modal de edición */}
      <Dialog open={openEditDialog} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {editingTipo && editingTipo.id <= 6 ? (
              <>
                <LockIcon sx={{ mr: 1, color: "warning.main" }} />
                Ver Tipo de Transacción (Solo Lectura)
              </>
            ) : (
              <>
                <EditIcon sx={{ mr: 1, color: "primary.main" }} />
                Editar Tipo de Transacción
              </>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              autoFocus
              label="Descripción"
              type="text"
              fullWidth
              variant="outlined"
              value={editDescripcion}
              onChange={(e) => setEditDescripcion(e.target.value)}
              disabled={editingTipo && editingTipo.id <= 6}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ReceiptLongIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editVerEnTransaccion}
                  onChange={(e) => setEditVerEnTransaccion(e.target.checked)}
                  color="primary"
                  disabled={editingTipo && editingTipo.id <= 6}
                />
              }
              label="Ver en Transacción"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editVerEnCaja}
                  onChange={(e) => {
                    setEditVerEnCaja(e.target.checked);
                    if (!e.target.checked) {
                      setEditVerEnColumna("");
                    }
                  }}
                  color="primary"
                  disabled={editingTipo && editingTipo.id <= 6}
                />
              }
              label="Ver en Caja"
            />
            {editVerEnCaja && (
              <FormControl fullWidth variant="outlined">
                <InputLabel id="edit-ver-en-columna-label">Columna en Caja</InputLabel>
                <Select
                  labelId="edit-ver-en-columna-label"
                  value={editVerEnColumna}
                  onChange={(e) => setEditVerEnColumna(e.target.value)}
                  label="Columna en Caja"
                  disabled={editingTipo && editingTipo.id <= 6}
                >
                  <MenuItem value="Ingreso">Ingreso</MenuItem>
                  <MenuItem value="Egreso">Egreso</MenuItem>
                </Select>
              </FormControl>
            )}
            {editingTipo && editingTipo.id <= 6 && (
              <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                <Typography variant="body2" color="warning.dark">
                  ⚠️ Este es un tipo de transacción del sistema (ID 1-6) y no puede ser modificado.
                  Puede ver su configuración pero no editarla.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseEditDialog} variant="outlined" disabled={loading}>
            {editingTipo && editingTipo.id <= 6 ? "Cerrar" : "Cancelar"}
          </Button>
          {editingTipo && editingTipo.id > 6 && (
            <Button
              onClick={handleUpdateTipo}
              variant="contained"
              disabled={loading}
              sx={{
                backgroundColor: "#5b5b5f",
                "&:hover": {
                  backgroundColor: alpha("#5b5b5f", 0.9),
                },
              }}
            >
              Actualizar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de eliminación */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", color: "error.main" }}>
            <DeleteIcon sx={{ mr: 1 }} />
            Confirmar Eliminación
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro que desea eliminar el tipo de transacción{" "}
            <strong>{deletingTipo?.descripcion}</strong>?
            <br />
            <br />
            Esta acción marcará el tipo como eliminado.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDeleteDialog} variant="outlined" disabled={loading}>
            Cancelar
          </Button>
          <Button onClick={handleDeleteTipo} variant="contained" color="error" disabled={loading}>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

